import React, { useState, useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import api from '../../services/api';
import GenericSelect from '../../components/ui/GenericSelect';
import TextInput from '../../components/ui/TextInput.jsx';
import Button from '../../components/ui/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import A11yDialog from '../../components/ui/A11yDialog';

// Self-contained move-to-stage dialog usable from anywhere a single application
// object is in hand (detail page, reference card). Mirrors the move flow on the
// list page (AtsApplicationsPage) but without the list-oriented hook: fetches
// the posting's effective stages, lets the user pick a target + optional note,
// and requires explicit confirmation for a backward (regression) move.
const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${(p) => (p.$open ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1300;
  padding: 24px;

  /* FE-08 — telefonda 24px x 2 = 48px, 390px ekranin %12'si. */
  @media (max-width: 600px) {
    padding: 12px;
  }
`;

const ModalCard = styled.div`
  background: #fff;
  border-radius: 8px;
  max-width: 480px;
  width: 100%;
  padding: 24px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  /* FE-08 — max-height YOKTU: perde "position: fixed; inset: 0" +
     "align-items: center" olduğundan, karttan uzun bir içerik ekranın ALTINDAN
     VE ÜSTÜNDEN taşıyordu ve hiçbir yolla kaydırılamıyordu — "Onayla"
     düğmesine telefonda hiç ulaşılamıyordu. "100%" perdenin iç kutusuna göre
     çözülür, yani perdenin iç boşluğu 24px'ten 12px'e inince kart otomatik
     olarak büyür (sabit 90vh bunu yapamaz).
     Bu, kabuğun "tek kaydırma" kuralını ÇİĞNEMEZ: diyalog ayrı bir katmandır,
     altındaki Main bu sırada zaten kilitlidir. */
  max-height: 100%;
  overflow-y: auto;
`;

const FieldGroup = styled.div`
  margin-bottom: 18px;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const DialogTitle = styled.h3`
  margin: 0 0 18px;
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
`;

export default function ApplicationStageMoveDialog({ open, application, preselectNext = false, onClose, onMoved }) {
  const { t } = useTranslation();
  const [stages, setStages] = useState([]);
  // Current stage's OrderIndex, captured before filtering it out — used to
  // detect backward moves (target OrderIndex < current).
  const [currentOrderIndex, setCurrentOrderIndex] = useState(null);
  const [stageId, setStageId] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [backwardConfirm, setBackwardConfirm] = useState(false);

  const jobPostingId = application?.jobPostingId;
  const currentStageId = application?.currentStageId;

  useEffect(() => {
    if (!open || !jobPostingId) return undefined;
    let cancelled = false;
    setStageId('');
    setNote('');
    setStages([]);
    setCurrentOrderIndex(null);
    setBackwardConfirm(false);
    api.get(`/v1/ats-pipeline-stages/effective-for-posting/${jobPostingId}`)
      .then((res) => {
        if (cancelled) return;
        const all = res.data?.data || [];
        const current = all.find((s) => s.id === currentStageId);
        const currentIdx = current ? current.orderIndex : null;
        setCurrentOrderIndex(currentIdx);
        const others = all.filter((s) => s.id !== currentStageId);
        setStages(others);
        // Guided "advance": auto-select the nearest forward stage so a single
        // confirm moves the candidate on. The user can still change it.
        if (preselectNext && currentIdx != null) {
          const forward = others
            .filter((s) => s.orderIndex > currentIdx)
            .sort((a, b) => a.orderIndex - b.orderIndex);
          if (forward.length) setStageId(forward[0].id);
        }
      })
      .catch(() => { if (!cancelled) setStages([]); });
    return () => { cancelled = true; };
  }, [open, jobPostingId, currentStageId, preselectNext]);

  const performMove = useCallback(async () => {
    if (!application?.id || !stageId) return;
    setBusy(true);
    try {
      await api.post(`/v1/ats-applications/${application.id}/move-to-stage`, {
        toStageId: stageId,
        reasonNote: note || undefined,
      });
      if (onMoved) onMoved();
      if (onClose) onClose();
    } finally {
      setBusy(false);
    }
  }, [application, stageId, note, onMoved, onClose]);

  // BP-26 — terminal "Elendi" aşamasına taşımak başvuruyu `disqualified` yapar ve
  // backend gerekçesiz isteği 400 ile reddeder (ats.application.disqualifyReasonRequired).
  const requiresReason = useMemo(() => {
    const target = stages.find((s) => s.id === stageId);
    return !!target && target.isTerminal === true && target.type === 'disqualified';
  }, [stages, stageId]);

  const handleConfirm = useCallback(() => {
    if (!stageId) return;
    if (requiresReason && !note.trim()) return;
    const target = stages.find((s) => s.id === stageId);
    const targetOrder = target ? target.orderIndex : null;
    const isBackward = currentOrderIndex != null && targetOrder != null && targetOrder < currentOrderIndex;
    if (isBackward) { setBackwardConfirm(true); return; }
    performMove();
  }, [stageId, stages, currentOrderIndex, performMove, requiresReason, note]);

  if (!application) return null;

  return (
    <>
      {/* Hide the move modal while the backward-confirm dialog is up so the two
          overlays don't stack. */}
      <A11yDialog
        open={!!open && !backwardConfirm}
        onClose={onClose}
        dirty={!!stageId || !!note.trim()}
        overlay={Modal}
        card={ModalCard}
        overlayProps={{ $open: true }}
      >
        {({ titleId }) => (
          <>
          <DialogTitle id={titleId}>
            {t('ats.application.moveStageTitle', { candidate: application.candidateFullName })}
          </DialogTitle>
          <FieldGroup>
            <GenericSelect
              label={t('ats.application.targetStage')}
              options={stages}
              value={stageId}
              onChange={(id) => setStageId(id || '')}
              placeholder={t('ats.application.selectStage')}
              required
            />
          </FieldGroup>
          <FieldGroup>
            <TextInput
              label={requiresReason ? t('ats.application.reason') : t('ats.application.reasonNote')}
              type="textarea"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              required={requiresReason}
            />
          </FieldGroup>
          <Actions>
            <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
            <Button
              onClick={handleConfirm}
              disabled={!stageId || busy || (requiresReason && !note.trim())}
              loading={busy}
            >
              {t('common.confirm')}
            </Button>
          </Actions>
          </>
        )}
      </A11yDialog>

      <ConfirmDialog
        isOpen={backwardConfirm}
        title={t('ats.application.backwardStageTitle')}
        message={t('ats.application.backwardStageConfirm')}
        onConfirm={() => { setBackwardConfirm(false); performMove(); }}
        onCancel={() => setBackwardConfirm(false)}
        loading={busy}
        type="danger"
      />
    </>
  );
}
